import { useEffect, useRef, useState } from 'react'

/**
 * The app's own keyboard. With the Bluetooth scanner paired, iPadOS treats it as a hardware keyboard and hides its own,
 * so every text field needed the little keyboard key. This one shows itself under any focused text field or textarea,
 * types into it the way a person would (the field keeps focus and its caret), and asks iPadOS not to show its keyboard
 * (`inputmode="none"`). Off with the "iPad keyboard" key; the choice is kept on the device.
 */
const PREF = 'starchive.softkb'
const HEIGHT = 262
type Shift = 'off' | 'once' | 'lock'
const ROWS = [['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'], ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'], ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'], ['z', 'x', 'c', 'v', 'b', 'n', 'm']]
const SYMS = [['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'], ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'], ['.', ',', '?', '!', "'", '%', '#', '+', '='], ['x', '*', '_', '~', '<', '>']]

export const softKeyboardOn = () => { try { return localStorage.getItem(PREF) !== 'off' } catch { return true } }
const setPref = (on: boolean) => { try { localStorage.setItem(PREF, on ? 'on' : 'off') } catch { /* fine */ } }

type Field = HTMLInputElement | HTMLTextAreaElement
const isField = (el: Element | null): el is Field => {
  if (!el) return false
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled && !el.hasAttribute('data-no-softkb')
  if (el instanceof HTMLInputElement) return ['text', 'search', 'tel', 'email', 'url', 'number', 'password', ''].includes(el.type === 'text' && !el.getAttribute('type') ? '' : el.type) && !el.readOnly && !el.disabled && !el.hasAttribute('data-no-softkb')
  return false
}
/** Puts text at the caret and tells React (an `input` event after a direct value change is what its onChange listens for). */
function typeInto(el: Field, text: string, backspace = false) {
  let s0: number | null = null, e0: number | null = null
  try { s0 = el.selectionStart; e0 = el.selectionEnd } catch { /* email and number fields have no caret */ }
  if (s0 === null || e0 === null) {
    // no caret API (type="email" / "number" throw on setRangeText): type at the end through the native setter so React sees it
    const next = backspace ? el.value.slice(0, -1) : el.value + text
    Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value')?.set?.call(el, next)
    el.dispatchEvent(new Event('input', { bubbles: true }))
    return
  }
  const start = s0, end = e0
  if (backspace) { if (start === end && start === 0) return; el.setRangeText('', start === end ? start - 1 : start, end, 'end') }
  else el.setRangeText(text, start, end, 'end')
  el.dispatchEvent(new Event('input', { bubbles: true }))
}
/** iPadOS chooses its keyboard when a field gets focus, so the attribute has to be on the field before then. */
function markFields(on: boolean) {
  document.querySelectorAll<Field>('input, textarea').forEach((el) => {
    if (!isField(el)) return
    if (on) { if (el.getAttribute('inputmode') !== 'none') { el.dataset.softkbMode = el.getAttribute('inputmode') ?? ''; el.setAttribute('inputmode', 'none') } }
    else if (el.dataset.softkbMode !== undefined) { if (el.dataset.softkbMode) el.setAttribute('inputmode', el.dataset.softkbMode); else el.removeAttribute('inputmode'); delete el.dataset.softkbMode }
  })
}

export function SoftKeyboard() {
  const [on, setOn] = useState(softKeyboardOn)
  const [field, setField] = useState<Field | null>(null)
  const [shift, setShift] = useState<Shift>('off')
  const [syms, setSyms] = useState(false)
  const hideTimer = useRef<number | null>(null)
  const lastShiftTap = useRef(0)

  // keep every text field marked so iPadOS keeps its keyboard down while ours is on
  useEffect(() => {
    markFields(on)
    if (!on) return
    const mo = new MutationObserver(() => markFields(true))
    mo.observe(document.body, { childList: true, subtree: true })
    return () => { mo.disconnect(); markFields(false) }
  }, [on])

  useEffect(() => {
    const show = (e: FocusEvent) => { const t = e.target as Element; if (isField(t)) { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } setField(t); setSyms(false); setShift('off') } }
    const hide = (e: FocusEvent) => {
      const next = e.relatedTarget as Element | null
      if (next && next.closest('.softkb')) return
      hideTimer.current = window.setTimeout(() => { setField(null); hideTimer.current = null }, 120)
    }
    document.addEventListener('focusin', show); document.addEventListener('focusout', hide)
    return () => { document.removeEventListener('focusin', show); document.removeEventListener('focusout', hide) }
  }, [])

  // make room: the content and any sheet move up so the field stays in view above the keyboard
  const open = on && !!field
  useEffect(() => {
    document.body.classList.toggle('softkb-open', open)
    document.body.style.setProperty('--softkb', open ? HEIGHT + 'px' : '0px')
    if (open && field) window.setTimeout(() => field.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60)
  }, [open, field])

  if (!on) return field ? <button type="button" className="softkb-restore" tabIndex={-1} onPointerDown={(e) => e.preventDefault()} onClick={() => { setPref(true); setOn(true) }}>Show the app keyboard</button> : null
  if (!field) return null
  const press = (e: React.PointerEvent) => e.preventDefault() // the field keeps focus
  const key = (ch: string) => {
    if (!field) return
    const out = shift !== 'off' ? ch.toUpperCase() : ch
    typeInto(field, out)
    if (shift === 'once') setShift('off')
  }
  const tapShift = () => { const now = Date.now(); if (now - lastShiftTap.current < 350) setShift('lock'); else setShift((s) => (s === 'off' ? 'once' : 'off')); lastShiftTap.current = now }
  const enter = () => { if (!field) return; if (field instanceof HTMLTextAreaElement) typeInto(field, '\n'); else { const form = field.form; if (form) form.requestSubmit(); else field.blur() } }
  const rows = syms ? SYMS : ROWS
  return (
    <div className="softkb" role="group" aria-label="Keyboard">
      {rows.map((r, i) => (
        <div key={i} className={'softkb__row' + (i === 3 ? ' softkb__row--last' : '')}>
          {i === 3 && <button type="button" tabIndex={-1} className={'softkb__key softkb__key--wide' + (shift !== 'off' ? ' on' : '')} onPointerDown={press} onClick={tapShift} aria-label="Shift">{shift === 'lock' ? '⇪' : '⇧'}</button>}
          {r.map((k) => <button key={k} type="button" tabIndex={-1} className="softkb__key" onPointerDown={press} onClick={() => key(k)}>{shift !== 'off' && !syms ? k.toUpperCase() : k}</button>)}
          {i === 3 && <button type="button" tabIndex={-1} className="softkb__key softkb__key--wide" onPointerDown={press} onClick={() => field && typeInto(field, '', true)} aria-label="Delete">⌫</button>}
        </div>
      ))}
      <div className="softkb__row">
        <button type="button" tabIndex={-1} className="softkb__key softkb__key--wide" onPointerDown={press} onClick={() => setSyms((s) => !s)}>{syms ? 'ABC' : '#+='}</button>
        <button type="button" tabIndex={-1} className="softkb__key softkb__key--wide" onPointerDown={press} onClick={() => key('.')}>.</button>
        <button type="button" tabIndex={-1} className="softkb__key softkb__key--space" onPointerDown={press} onClick={() => key(' ')}>space</button>
        <button type="button" tabIndex={-1} className="softkb__key softkb__key--wide" onPointerDown={press} onClick={enter}>{field instanceof HTMLTextAreaElement ? 'return' : 'done'}</button>
        <button type="button" tabIndex={-1} className="softkb__key softkb__key--wide softkb__key--dim" onPointerDown={press} onClick={() => { field?.blur(); setPref(false); setOn(false) }}>iPad keyboard</button>
      </div>
    </div>
  )
}
